/**
 * AtlasNightSkyBodiesModule — REAL MESHES in the night sky (2026-08-27 pt2):
 *
 *   STATION   the actual `prop:space_station` GLB crossing the sky every six
 *             minutes (night_sky_schedule.stationAt drives it) — "very tiny,
 *             not as tiny as a dot": ~1.2° of sky, slowly tumbling, its own
 *             albedo unlit-warm (the station IS in sunlight up there even
 *             when the ground is dark).
 *   CRUISER   the `prop:ship_sovereign` capital hull during the shared war
 *             window (battleSkyAt — the same clock the space sector fights
 *             on), drifting along its heading; the shader battle layer keeps
 *             the weapon flashes + escort train around it.
 *   PLANETS   sys0's REAL worlds (2026-10-06) — Cindral, Vorune, Palewater
 *             and Cindral's moon Ashfall, the sector's own spheres and
 *             textures — placed where the sector puts them as seen from the
 *             Commons (night_sky_schedule `planetsAt`: the true elongation and
 *             ecliptic latitude, folded through the sun's own diurnal path),
 *             sized by their TRUE angular diameters under ONE global scale
 *             that puts the largest (Vorune) at 1.0° — twice the Moon, never
 *             more (the 2026-09-08 "floating structure over the town" was a
 *             4.4° disc + halo) — and lit from the drawn sun, so each shows
 *             its true phase. Their luminance is the Moon's per unit albedo
 *             taken through THE PHYSICAL-EMISSIVE CONTRACT (the old discs were
 *             fixed scene units: a Moon-bright crescent read as a 2-px smudge
 *             at dusk), seen through the air toward them. Additive: the dark
 *             limb is the sky behind, and a world near the sun drowns in the
 *             twilight it is added to. Each world's pole stands on the
 *             ecliptic pole, so Vorune's bands lie along the sun's path.
 *             Planets hold STEADY (no twinkle) against the shimmering stars —
 *             exactly how you tell a planet from a star with the naked eye.
 *
 * Rendering doctrine (the sky-traffic/volcanic-planet recon, all four traps):
 *   · Anchored to `atlas.localX/Y/Z` + dir·R each frame (origin-rebase-safe;
 *     `camera.getWorldPosition` is render-space and drifts past 10 km).
 *   · R = 3600 m — inside the 5200 m far plane, far above the 900–1500 m
 *     cloud band; meshes are OPAQUE with depthWrite ON so the mesh-cloud
 *     field (transparent, depthTest on) correctly composites OVER them.
 *   · `material.fog = false` on EVERY material — night FogExp2 at 3.6 km is
 *     e^-(6.5)² ≈ total; this is the first fog opt-out in the client.
 *   · `frustumCulled = false`; never registered with RenderCullingModule.
 *   · Unlit (MeshBasicMaterial keeping the GLB albedo maps): scene lights
 *     are ground-local; sky bodies carry their own sunlight.
 */
import * as THREE from 'three/webgpu';
import { dot, float, materialColor, max, normalWorld, smoothstep, uniform, vec4 } from 'three/tsl';
import type { App, IModule } from 'enginex';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { atlas } from '../state.js';
import { makeSystem, Phase, Sub } from '../engine-compat.js';
import { prefabAssetId } from '../lib/player_models.js';
import { markShared } from '../lib/defer_dispose.js';
import { lookAtLocal } from '../lib/local_origin.js';
import { currentTod, warInputsFor, warObserver, warSkySummaryNow } from './SkyModule.js';
// space campaign L (2026-10-01): the war's ships COUNTED from the server summary, riding the planet's rotation.
import { WarSkyBatch } from '../lib/sky/war_sky_batch.js';
import { silhouetteBudget } from '@shared/war_sky.js';
import { nightFactor, sunDirection, sunObserver } from '../lib/lighting.js';
import { lightFrame, emissiveNits, lum709, MOON_DISC_MAX_OVER_KEY_EV } from '../lib/photometry.js';
import { skySampler } from '../lib/sky/sky_sampler.js';
import {
  stationAt, battleSkyAt, planetsAt, sys0SkyPlanets, eclipticToSky, warSkyLayout, type Vec3, type SkyPlanetDef, type PlanetObserver,
} from '../lib/night_sky_schedule.js';
import type { AssetModule } from './AssetModule.js';

const R = 3600;                    // sky anchor distance (angular size is all that matters)
const STATION_DEG = 1.2;           // station angular diameter
const CRUISER_DEG = 1.6;           // capital hull angular length
const deg2size = (deg: number): number => 2 * R * Math.tan((deg * Math.PI / 180) / 2);

/** Box-fit an object so its LONGEST axis spans `size` meters. */
function fitLongest(obj: THREE.Object3D, size: number): void {
  const box = new THREE.Box3().setFromObject(obj);
  const s = new THREE.Vector3(); box.getSize(s);
  const longest = Math.max(s.x, s.y, s.z, 0.001);
  obj.scale.setScalar(size / longest);
}

/** Replace PBR materials with unlit fog-free ones keeping the albedo maps. */
function unlitify(obj: THREE.Object3D, tint: THREE.Color): void {
  const conv = (old: THREE.Material): THREE.Material => {
    const std = old as THREE.MeshStandardMaterial;
    const mat = new THREE.MeshBasicMaterial({ map: std.map ?? null, color: tint.clone() });
    mat.fog = false;
    return mat;
  };
  obj.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    m.material = Array.isArray(m.material) ? (m.material.map(conv) as unknown as THREE.Material) : conv(m.material as THREE.Material);
    m.frustumCulled = false;
  });
}

interface PlanetMesh {
  def: SkyPlanetDef; disc: THREE.Mesh; mat: THREE.MeshBasicNodeMaterial;
  /** 1 ÷ the face texture's mean linear luminance once it lands (the face then carries albedo AROUND its mean); 0 = untextured. */
  texScale: number;
  /** Last frame's luminance seen through the air (cd/m²) and the scene-units multiplier it became (diagnostics). */
  nitsSeen: number; scene: number;
}
const RAD2DEG = 180 / Math.PI;
/** A planet face whose pixels cannot be read (a tainted or stripped image) is taken at this mean linear luminance. */
const FALLBACK_TEX_MEAN = 0.3;
/** The mean LINEAR luminance of an sRGB image (a 32×16 thumbnail is plenty for a face's mean) — null if unreadable. */
function meanLinearLuminance(img: unknown): number | null {
  try {
    const src = img as CanvasImageSource & { width?: number; height?: number };
    if (!src || !((src.width ?? 0) > 0)) return null;
    const c = new OffscreenCanvas(32, 16);
    const g = c.getContext('2d');
    if (!g) return null;
    g.drawImage(src, 0, 0, 32, 16);
    const d = g.getImageData(0, 0, 32, 16).data;
    const lin = (v: number): number => { const x = v / 255; return x <= 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4; };
    let sum = 0;
    for (let i = 0; i < d.length; i += 4) sum += 0.2126 * lin(d[i]) + 0.7152 * lin(d[i + 1]) + 0.0722 * lin(d[i + 2]);
    const m = sum / (d.length / 4);
    return m > 0.005 && Number.isFinite(m) ? m : null;
  } catch { return null; }
}

export class AtlasNightSkyBodiesModule implements IModule {
  readonly name = 'AtlasNightSkyBodies';
  private app: App | null = null;
  private assets: AssetModule | null = null;
  private station: THREE.Object3D | null = null;
  private cruiser: THREE.Object3D | null = null;
  /** Space campaign L: the counted fleet (one instanced batch, ≤ 98 silhouettes). */
  private warBatch: WarSkyBatch | null = null;
  private planets: PlanetMesh[] = [];
  /** The drawn sun's sky direction — every world is lit from it (parallel rays: the sector's sun is at infinity). */
  private readonly uSun = uniform(new THREE.Vector3(0, 1, 0));
  /** The brightest world's lit-face luminance (cd/m²): the exposure cap is taken for it, so every world keeps its true ratio. */
  private nitsRef = 1;
  private readonly _up = new THREE.Vector3(0, 1, 0);
  private readonly _pole = new THREE.Vector3();
  private tried = false;
  private readonly _v = new THREE.Vector3();

  init(app: App): void {
    this.app = app;
    this.assets = app.getModule('AssetModule') as AssetModule | null;
    // Planet discs need no GLBs — build them now; textures stream in after.
    this.buildPlanets(app.world.scene);
    this.warBatch = new WarSkyBatch();
    app.world.scene.add(this.warBatch.mesh);
    app.world.addSystem(makeSystem('NightSkyBodies', Phase.RenderSub, 6, () => this.tick(), Sub.Update));
    (globalThis as Record<string, unknown>).__nightSkyBodies = () => {
      // Probe: where a body is and how big it LOOKS (its longest world axis
      // over its distance from the player, in degrees) — the "floating
      // structure over the town" report (2026-09-08) was a sky body.
      const body = (o: THREE.Object3D | null): { visible: boolean; at: number[]; distM: number; longestM: number; apparentDeg: number; yaw: number; pitch: number } | null => {
        if (!o) return null;
        const b = new THREE.Box3().setFromObject(o); const sz = new THREE.Vector3(); b.getSize(sz);
        const dx = o.position.x - atlas.localX, dy = o.position.y - (atlas.localY + 1.6), dz = o.position.z - atlas.localZ;
        const d = Math.hypot(dx, dy, dz);
        const longest = Math.max(sz.x, sz.y, sz.z);
        return {
          visible: o.visible, at: [Math.round(o.position.x), Math.round(o.position.y), Math.round(o.position.z)], distM: Math.round(d), longestM: +longest.toFixed(1),
          apparentDeg: +((2 * Math.atan(longest / 2 / Math.max(1, d))) * 180 / Math.PI).toFixed(2),
          yaw: +Math.atan2(-dx, -dz).toFixed(3), pitch: +Math.atan2(dy, Math.hypot(dx, dz)).toFixed(3), // forward = (−sin yaw, −cos yaw)
        };
      };
      return {
        station: !!this.station, cruiser: !!this.cruiser,
        stationVisible: this.station?.visible ?? false,
        cruiserVisible: this.cruiser?.visible ?? false,
        stationBody: body(this.station), cruiserBody: body(this.cruiser),
        warBatch: this.warBatch ? { visible: this.warBatch.mesh.visible, drawn: this.warBatch.drawn, byClass: { ...this.warBatch.byClass }, count: this.warBatch.mesh.count, dir: { ...this.warBatch.lastDir } } : null,
        planets: this.planets.map((p) => ({
          name: p.def.name, visible: p.disc.visible, y: Math.round(p.disc.position.y - atlas.localY),
          trueDeg: +p.def.trueDeg.toFixed(3), discDeg: +p.def.discDeg.toFixed(4), point: p.def.point,
          elongDeg: +(p.def.elong * RAD2DEG).toFixed(2), eastDeg: +(p.def.east * RAD2DEG).toFixed(2), latDeg: +(p.def.lat * RAD2DEG).toFixed(2),
          phaseDeg: +(p.def.phase * RAD2DEG).toFixed(2), illum: +p.def.illum.toFixed(3), nits: Math.round(p.def.nits),
          nitsSeen: Math.round(p.nitsSeen), scene: +p.scene.toExponential(3), texScale: +p.texScale.toFixed(2),
          ...(body(p.disc) ?? {}),
          // A sphere's angular diameter from its RADIUS: the generic box measure reads a rotated sphere up to √2 wide.
          apparentDeg: (() => {
            const rad = ((p.disc.geometry as THREE.SphereGeometry).parameters?.radius ?? 0) * p.disc.scale.x;
            const d = Math.hypot(p.disc.position.x - atlas.localX, p.disc.position.y - (atlas.localY + 1.6), p.disc.position.z - atlas.localZ);
            return +((2 * Math.asin(Math.min(1, rad / Math.max(1, d)))) * RAD2DEG).toFixed(3);
          })(),
        })),
      };
    };
    // Probe lever: the pure sky of sys0's worlds at any hour / season / latitude (defaults: now, the shared observer) —
    // a probe picks the hour a world is up from this, then pins it.
    (globalThis as Record<string, unknown>).__nightSkyPlanets = (tod?: number, dayOfYear?: number, latDeg?: number) => {
      const o = sunObserver();
      const obs: PlanetObserver = { latDeg: latDeg ?? o.latDeg, dayOfYear: dayOfYear ?? o.dayOfYear };
      const t = tod ?? currentTod();
      const table = sys0SkyPlanets();
      const altAz = (d: Vec3): { altDeg: number; azDeg: number } => ({
        altDeg: +(Math.asin(Math.max(-1, Math.min(1, d.y))) * RAD2DEG).toFixed(2),
        azDeg: +((Math.atan2(d.x, -d.z) * RAD2DEG + 360) % 360).toFixed(1), // from north (−Z) through east (+X)
      });
      const sun = eclipticToSky(t, obs.latDeg, obs.dayOfYear, 0, 0);
      return {
        tod: t, observer: obs, scale: table.scale, sun: { ...altAz(sun), dir: sun, night: nightFactor(sun.y) },
        planets: planetsAt(t, obs, table).map((p) => ({
          name: p.name, ...altAz(p.dir), dir: p.dir,
          trueDeg: p.trueDeg, discDeg: p.discDeg, elongDeg: p.elong * RAD2DEG, eastDeg: p.east * RAD2DEG, latDeg: p.lat * RAD2DEG,
          phaseDeg: p.phase * RAD2DEG, illum: p.illum, nits: p.nits, flux: p.flux, glare: p.glare,
        })),
      };
    };
  }

  /** GLBs load lazily once the connection is live (prefab registry replicated). */
  private ensureModels(): void {
    if (this.tried || !this.assets || !atlas.conn) return;
    const conn = atlas.conn;
    const stId = prefabAssetId(conn, 'prop:space_station');
    const cvId = prefabAssetId(conn, 'prop:ship_sovereign');
    if (stId === null && cvId === null) return; // registry not replicated yet — retry next tick
    this.tried = true;
    const scene = this.app?.world.scene;
    if (!scene) return;
    if (stId !== null) {
      void this.assets.load(stId).then((master) => {
        markShared(master);
        const st = master.clone();
        unlitify(st, new THREE.Color(1.25, 1.2, 1.1)); // warm sunlit metal
        fitLongest(st, deg2size(STATION_DEG));
        st.visible = false;
        scene.add(st);
        this.station = st;
      }).catch(() => { /* model unavailable — the sky just has no station mesh */ });
    }
    if (cvId !== null) {
      void this.assets.load(cvId).then((master) => {
        markShared(master);
        const cv = master.clone();
        unlitify(cv, new THREE.Color(1.1, 1.05, 0.95));
        fitLongest(cv, deg2size(CRUISER_DEG));
        cv.visible = false;
        scene.add(cv);
        this.cruiser = cv;
      }).catch(() => { /* the shader battle layer still shows the engagement */ });
    }
  }

  private buildPlanets(scene: THREE.Scene): void {
    // The sector is static, so every disc's size is fixed at build: its TRUE angular diameter × the one global scale.
    // The phase: lit from the drawn sun (a soft terminator, a little Lambert falloff for a round body); ADDITIVE, so the
    // night limb shows the sky behind it and twilight drowns a world near the sun (the old opaque disc drew a dark
    // coin on a bright dusk). No halo shell: the terminator reads as a body, and nothing is drawn wider than the disc.
    const ndl = dot(normalWorld, this.uSun);
    const shade = smoothstep(float(-0.06), float(0.16), ndl).mul(float(0.55).add(max(ndl, float(0)).mul(0.45)));
    const table = sys0SkyPlanets();
    this.nitsRef = Math.max(1, ...table.planets.map((d) => d.nits));
    for (const def of table.planets) {
      const r = deg2size(def.discDeg) / 2;
      const mat = new THREE.MeshBasicNodeMaterial();
      mat.color.setRGB(0, 0, 0); // written every frame (tick) — the luminance moves with the hour and the metering
      mat.colorNode = vec4((materialColor as unknown as { rgb: typeof shade }).rgb.mul(shade), float(1)) as never;
      mat.transparent = true;
      mat.blending = THREE.AdditiveBlending;
      mat.depthWrite = false;
      mat.fog = false;
      markMrtOverlay(mat); // THE OVERLAY LAW: an additive body over the sky never rewrites the G-buffer behind it
      const disc = new THREE.Mesh(new THREE.SphereGeometry(r, 32, 20), mat);
      disc.name = `sky-planet:${def.name}`;
      disc.frustumCulled = false;
      disc.visible = false;
      scene.add(disc);
      const pm: PlanetMesh = { def, disc, mat, texScale: 0, nitsSeen: 0, scene: 0 };
      this.planets.push(pm);
      // Stream the real planet face in (the same MinIO textures the space
      // sector renders when you fly there) — its colour is the world's own (no tint over it), its mean is the albedo.
      void this.assets?.loadTexture(`textures/planets/${def.tex}.png`).then((t) => {
        pm.texScale = 1 / (meanLinearLuminance(t.image) ?? FALLBACK_TEX_MEAN);
        mat.map = t; mat.needsUpdate = true;
      }).catch(() => { /* the tinted disc is a fine fallback */ });
    }
  }

  private tick(): void {
    if (atlas.spaceFlight || atlas.currentWorldId === null) {
      this.hideAll();
      return;
    }
    this.ensureModels();
    const tod = currentTod();
    const nf = nightFactor(sunDirection(tod).y);
    if (nf < 0.04) { this.hideAll(); return; }
    const nowS = Date.now() / 1000;
    const ax = atlas.localX, ay = atlas.localY, az = atlas.localZ;
    const place = (obj: THREE.Object3D, dir: Vec3): void => {
      obj.position.set(ax + dir.x * R, ay + dir.y * R, az + dir.z * R);
    };
    // ── The station transit ──
    if (this.station) {
      const st = stationAt(nowS);
      const on = st.bright > 0.12 && st.dir.y > 0.02;
      this.station.visible = on;
      if (on) {
        place(this.station, st.dir);
        const t = nowS * 0.13;
        this.station.rotation.set(t * 0.4, t, 0); // slow tumble
      }
    }
    // ── The war cruiser ──
    // The treaty + campaign rows the shader layer reads (SkyModule, once a second, cached) —
    // a ceasefire or a resolved war ends the cruiser's pass with the flashes around it.
    // Space campaign L: + the diurnal fold (the war rises and sets), and with a summary row the fleet is COUNTED — the
    // instanced silhouettes take over from the one cruiser (capitals per side from `capsAlive`, the escorts, the pool).
    const bs = battleSkyAt(nowS, warInputsFor(nowS), warObserver());
    const ws = warSkySummaryNow(nowS);
    const counted = !!ws && ws.state !== 'ceasefire' && !!this.warBatch;
    if (this.warBatch) {
      const show = counted && bs.active > 0.02 && bs.dir.y > 0.02 && nf > 0.3;
      if (show && ws) this.warBatch.update(bs.dir, bs.tangent, warSkyLayout(silhouetteBudget(ws), ws.cyc, bs.t), { x: ax, y: ay, z: az }, bs.active * Math.min(1, nf * 1.4));
      else this.warBatch.hide();
    }
    if (this.cruiser) {
      const on = !counted && bs.active > 0.25 && bs.dir.y > 0.05 && nf > 0.5;
      this.cruiser.visible = on;
      if (on) {
        place(this.cruiser, bs.dir);
        // Nose along the heading tangent (hulls nose −Z after lookAt).
        this._v.set(ax + (bs.dir.x + bs.tangent.x * 0.1) * R, ay + (bs.dir.y + bs.tangent.y * 0.1) * R, az + (bs.dir.z + bs.tangent.z * 0.1) * R);
        lookAtLocal(this.cruiser, this._v); // THE SCENE SHIFT: lookAt aims from the WORLD position — an absolute target pointed the hull at the rebase origin past 10 km, not along its heading
      }
    }
    // ── The planets: sys0's worlds where the sector puts them (fixed elongations, the sun's own diurnal path) ──
    const sun = sunDirection(tod);
    this.uSun.value.set(sun.x, sun.y, sun.z);
    const obs = sunObserver();
    const ps = planetsAt(tod, obs);
    // Every world's pole on the ecliptic pole (the bands of a gas giant lie along the sun's path, as Jupiter's do).
    const pole = eclipticToSky(tod, obs.latDeg, obs.dayOfYear, 0, Math.PI / 2);
    this._pole.set(pole.x, pole.y, pole.z);
    // THE PHYSICAL-EMISSIVE CONTRACT (the Moon's own path, SkyModule.driveMoonSky): a lit face of `nits` cd/m² is
    // `emissiveNits` scene units, capped MOON_DISC_MAX_OVER_KEY_EV over the key. The cap is taken for the BRIGHTEST world
    // and the same per-nit factor serves all, so a night that caps the brightest keeps every world's true ratio to it.
    const perNit = emissiveNits(this.nitsRef, MOON_DISC_MAX_OVER_KEY_EV) / this.nitsRef;
    const sky = skySampler();
    const Tz = sky.transmittance(1);
    const tz = Math.max(1e-6, lum709(Tz.x, Tz.y, Tz.z));
    // Through the cloud deck: the transmittance the sun's disc and the moon take (photometry tCloud, 0.03 at full overcast).
    const deck = lightFrame.valid && lightFrame.model ? lightFrame.model.tCloud : 1;
    // Twilight: the worlds fade in with the night (bright ones pierce twilight first, like Venus at dusk).
    const dusk = Math.min(1, nf * 1.6);
    for (let i = 0; i < this.planets.length; i++) {
      const pm = this.planets[i], p = ps[i];
      const on = !!p && p.dir.y > -0.02 && p.glare > 0.002;
      pm.disc.visible = on;
      if (!on) { pm.scene = 0; continue; }
      place(pm.disc, p.dir);
      pm.disc.quaternion.setFromUnitVectors(this._up, this._pole);
      // The air toward the world: a rising world dims and reddens like the Moon (luminance relative to the zenith's).
      const T = sky.transmittance(Math.max(0, p.dir.y));
      const cl = Math.max(1e-9, lum709(T.x, T.y, T.z));
      pm.nitsSeen = p.nits * (cl / tz);
      pm.scene = pm.nitsSeen * perNit * dusk * p.glare * deck;
      // Textured: the face's own colour, its mean taken to 1 (the face is the albedo's map); untextured: the class tint.
      const tl = Math.max(1e-6, lum709(p.tint.r, p.tint.g, p.tint.b));
      const k = pm.texScale > 0 ? pm.scene * pm.texScale / cl : pm.scene / (cl * tl);
      const cr = pm.texScale > 0 ? 1 : p.tint.r, cg = pm.texScale > 0 ? 1 : p.tint.g, cb = pm.texScale > 0 ? 1 : p.tint.b;
      pm.mat.color.setRGB(T.x * cr * k, T.y * cg * k, T.z * cb * k);
    }
  }

  private hideAll(): void {
    if (this.station) this.station.visible = false;
    if (this.cruiser) this.cruiser.visible = false;
    this.warBatch?.hide();
    for (const p of this.planets) p.disc.visible = false;
  }

  dispose(): void {
    const scene = this.app?.world.scene;
    if (this.station) scene?.remove(this.station);
    if (this.cruiser) scene?.remove(this.cruiser);
    this.warBatch?.dispose(); this.warBatch = null;
    for (const p of this.planets) { scene?.remove(p.disc); p.disc.geometry.dispose(); p.mat.dispose(); }
    this.planets = [];
  }
}
